import { useState } from "react";

import { formatMarketBias, formatRiskLevel } from "../utils/marketLabels";

function formatIndiaBiasDetails(indiaBiasDetails) {
  if (!indiaBiasDetails) return "";
  if (typeof indiaBiasDetails === "string") return indiaBiasDetails;

  return indiaBiasDetails.narration
    ?? indiaBiasDetails.summary
    ?? indiaBiasDetails.indiaBiasNarration
    ?? indiaBiasDetails.indiaBiasSummary
    ?? indiaBiasDetails.tradingInstruction
    ?? indiaBiasDetails.instruction
    ?? [
      indiaBiasDetails.bias ? `India bias is ${indiaBiasDetails.bias}` : "",
      indiaBiasDetails.opening ? `opening is ${indiaBiasDetails.opening}` : "",
      indiaBiasDetails.riskLevel ? `risk is ${indiaBiasDetails.riskLevel}` : "",
    ].filter(Boolean).join(", ");
}

function formatValue(value, suffix = "") {
  if (value === null || value === undefined || value === "") return "-";
  return `${value}${suffix}`;
}

function formatUpdatedAt(value) {
  if (!value) return "";
  return `Updated ${value} IST`;
}

function metricToneClass(label, value) {
  const normalizedLabel = String(label ?? "").toLowerCase();
  const normalizedValue = String(value ?? "").toLowerCase();

  if (normalizedLabel.includes("risk") || normalizedValue.includes("elevated") || normalizedValue.includes("high")) {
    return "metric-tone-risk";
  }

  if (normalizedValue.includes("bearish") || normalizedValue.includes("defensive")) {
    return "metric-tone-negative";
  }

  if (normalizedValue.includes("bullish") || normalizedValue.includes("aggressive")) {
    return "metric-tone-positive";
  }

  if (normalizedLabel.includes("strategy")) {
    return "metric-tone-strategy";
  }

  return "metric-tone-neutral";
}

export default function TradingTableStrip({ marketBias }) {
  const [briefExpanded, setBriefExpanded] = useState(false);
  const drivers = [
    { label: "Bias", value: formatMarketBias(marketBias?.bias) },
    { label: "Risk", value: formatRiskLevel(marketBias?.riskLevel) },
    { label: "Opening", value: marketBias?.opening ?? "-" },
    { label: "Confidence", value: formatValue(marketBias?.confidence, "%") },
    { label: "Trend Day", value: formatValue(marketBias?.trendDayProbability, "%") },
    { label: "Sideways", value: formatValue(marketBias?.sidewaysProbability, "%") },
    { label: "Reversal Risk", value: formatValue(marketBias?.reversalRisk, "%") },
  ];
  const marketBiasNarration = marketBias?.marketBiasNarration ?? marketBias?.summary ?? "Waiting for market state.";
  const updatedAt = formatUpdatedAt(marketBias?.generatedAt);
  const indiaBiasSummary = formatIndiaBiasDetails(marketBias?.indiaBiasDetails);
  const snapshotNarrations = [
    { label: "India Bias", value: indiaBiasSummary, mobilePriority: true },
    { label: "Risk", value: marketBias?.riskNarration },
    { label: "Opening", value: marketBias?.openingNarration, mobilePriority: true },
    { label: "Global Support", value: marketBias?.globalSupportNarration },
    { label: "Weekly Trend", value: marketBias?.weeklyTrendNarration },
    { label: "Trading Instruction", value: marketBias?.tradingInstruction, mobilePriority: true },
  ].filter((item) => item.value);

  return (
    <section className="desk-card trading-table-strip">
      {!!updatedAt && <div className="market-freshness">{updatedAt}</div>}

      <div className="decision-strip-grid">
        {drivers.map((item) => (
          <div key={item.label} className={`decision-strip-cell ${metricToneClass(item.label, item.value)}`}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="table-focus table-focus-strip">
        <div>
          <span className="table-kicker">Analyst Brief</span>
          <h2>{marketBias?.bias ? `${formatMarketBias(marketBias.bias)} Market` : "Waiting"}</h2>
          <p>{marketBiasNarration}</p>
          {!!snapshotNarrations.length && (
            <div className={`snapshot-narration-list ${briefExpanded ? "brief-expanded" : ""}`}>
              {snapshotNarrations.map((item) => (
                <p
                  key={item.label}
                  className={[
                    item.label === "Trading Instruction" ? "snapshot-instruction" : "",
                    item.mobilePriority ? "" : "brief-mobile-optional",
                  ].filter(Boolean).join(" ")}
                >
                  <span>{item.label}</span>
                  {item.value}
                </p>
              ))}
              {snapshotNarrations.some((item) => !item.mobilePriority) && (
                <button
                  type="button"
                  className="brief-toggle"
                  onClick={() => setBriefExpanded((current) => !current)}
                >
                  {briefExpanded ? "Show less" : "Show full brief"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
